import {
  RevisionContent,
  RevisionAction,
  TemplateDetails,
  TemplateRevision,
} from "./types";

/**
 * Everything that depends on the shape of the templates API lives here, so a
 * change on the back end is a change in one file.
 */

/** `labels` comes back as null when a template has none. */
const toStringArray = (value: any): string[] =>
  Array.isArray(value) ? value.filter((item) => typeof item === "string") : [];

const toAction = (value: any): RevisionAction =>
  String(value).toUpperCase() === "CREATE" ? "CREATE" : "UPDATE";

/**
 * Newest first within the requested date range. The revision at the top
 * carries the "Latest Revision" label.
 */
export const mapRevisions = (raw: any): TemplateRevision[] => {
  if (!Array.isArray(raw)) return [];

  return raw
    .map((item: any) => ({
      revisionId: item?.revision_id ?? item?.revision_uid ?? "",
      name: item?.revision_name ?? item?.template_name ?? "",
      action: toAction(item?.action),
      isCurrent: false,
      createdBy: item?.created_by ?? "",
      createdAt: item?.created ?? "",
      modifiedBy: item?.modified_by ?? "",
      modifiedAt: item?.modified ?? "",
    }))
    .filter((revision) => revision.revisionId)
    .sort(
      (a, b) =>
        new Date(b.modifiedAt).getTime() - new Date(a.modifiedAt).getTime(),
    )
    .map((revision, index) => ({ ...revision, isCurrent: index === 0 }));
};

/** GET /asset_management/design/templates/{uid}/ → form state. */
export const mapTemplateDetails = (raw: any): TemplateDetails => ({
  sharedUid: raw?.shared_uid ?? "",
  name: raw?.name ?? raw?.template_name ?? "",
  componentSharedUid: raw?.component?.shared_uid ?? "",
  componentName: raw?.component?.name ?? "",
  labels: toStringArray(raw?.labels),
  html: raw?.html ?? "",
  css: raw?.css ?? "",
  additionalInfo: normaliseAdditionalInfo(raw?.additional_info),
  revisions: mapRevisions(raw?.revisions),
});

/** Same endpoint with `?revision=` → the body of one revision. */
export const mapRevisionContent = (raw: any): RevisionContent => ({
  html: raw?.html ?? "",
  css: raw?.css ?? "",
  additionalInfo: normaliseAdditionalInfo(raw?.additional_info),
  labels: toStringArray(raw?.labels),
  componentSharedUid: raw?.component?.shared_uid ?? "",
});

/**
 * `additional_info` arrives as a JSON string, sometimes double-encoded with
 * escaped quotes. Mirrors the unwrapping the current Edit screen does.
 */
export function normaliseAdditionalInfo(value: any): string {
  if (typeof value !== "string") return "{}";

  let text = value
    .replace(/\\n/g, "")
    .replace(/\\\\/g, "")
    .replace(/\\"/g, '"');
  if (text.startsWith('"') && text.endsWith('"')) {
    text = text.slice(1, -1);
  }
  return text || "{}";
}

/** Form state → PUT body for editTemplate. */
export const toEditPayload = (form: {
  name: string;
  componentSharedUid: string;
  html: string;
  css: string;
  additionalInfo: string;
  labels: string[];
}) => ({
  name: form.name,
  component: form.componentSharedUid,
  html: form.html,
  css: form.css,
  additional_info: form.additionalInfo,
  labels: form.labels,
});
